import { z } from "zod";
/**
 * What a demo account is filled with beyond the menu: the things a prospect
 * recognises as *theirs* when they open the demo — their events, their
 * memberships and pricing, their class schedule, their services. Read from
 * their website and the web where possible, generated as plausible samples
 * otherwise, and always capped so a 100-location franchise stays buildable.
 *
 * One `LocationContent` per location (a location's own page / store may list
 * its own schedule and events) plus one shared set on the prospect that is
 * pushed to every location that has nothing of its own.
 */
export const CONTENT_CAP = 100;
export const DemoEvent = z.object({
    title: z.string().min(1),
    description: z.string().default(""),
    /** ISO timestamps. */
    startsAt: z.string(),
    endsAt: z.string(),
    venue: z.string().default(""),
    address: z.string().default(""),
    /** Ticket price in dollars; 0 = free / RSVP only. */
    price: z.number().min(0).default(0),
    capacity: z.number().int().min(0).default(50),
    imageUrl: z.string().url().nullish(),
    category: z
        .enum(["general", "birthday", "promotion", "tournament", "fundraiser"])
        .default("general"),
    sourceUrl: z.string().optional(),
});
export const DemoMembership = z.object({
    name: z.string().min(1),
    description: z.string().default(""),
    /** Price per interval in dollars ("once" = paid in full). */
    price: z.number().min(0),
    interval: z.enum(["week", "month", "year", "once"]).default("month"),
    /** Contract length. */
    durationMonths: z.number().int().min(1).max(60).default(12),
    benefits: z.array(z.string()).default([]),
    /** Sign-up / registration fee, if their site lists one. */
    regFee: z.number().min(0).default(0),
    sourceUrl: z.string().optional(),
});
export const DemoClass = z.object({
    name: z.string().min(1),
    description: z.string().default(""),
    /** Weekdays, 0 = Sunday. */
    days: z.array(z.number().int().min(0).max(6)).min(1),
    /** 24h "HH:MM" local time. */
    startTime: z.string().regex(/^\d{2}:\d{2}$/),
    durationMin: z.number().int().min(15).max(480).default(60),
    level: z.string().default(""),
    ages: z.string().default(""),
    instructor: z.string().default(""),
    capacity: z.number().int().min(1).default(20),
    /** Drop-in price; 0 = included with membership. */
    price: z.number().min(0).default(0),
    sourceUrl: z.string().optional(),
});
export const DemoService = z.object({
    name: z.string().min(1),
    description: z.string().default(""),
    category: z.string().default(""),
    durationMin: z.number().int().min(5).max(480).default(60),
    price: z.number().min(0),
    sourceUrl: z.string().optional(),
});
export const ContentOrigin = z.enum(["site", "search", "sample", "mixed", "manual"]);
export const LocationContent = z.object({
    events: z.array(DemoEvent).max(CONTENT_CAP).default([]),
    memberships: z.array(DemoMembership).max(CONTENT_CAP).default([]),
    classes: z.array(DemoClass).max(CONTENT_CAP).default([]),
    services: z.array(DemoService).max(CONTENT_CAP).default([]),
    /** Where it came from: their site, web search, generated samples, or a mix. */
    origin: ContentOrigin.default("sample"),
    /** Pages the real items were read from. */
    sourceUrls: z.array(z.string()).default([]),
    generatedAt: z.string().optional(),
});
export const emptyContent = () => ({
    events: [],
    memberships: [],
    classes: [],
    services: [],
    origin: "manual",
    sourceUrls: [],
});
export const contentCounts = (c) => ({
    events: c?.events.length ?? 0,
    memberships: c?.memberships.length ?? 0,
    classes: c?.classes.length ?? 0,
    services: c?.services.length ?? 0,
});
export const contentIsEmpty = (c) => !c || (!c.events.length && !c.memberships.length && !c.classes.length && !c.services.length);
/**
 * How MyManager should model the organization: a plain organization (one
 * account) or a franchisor (the org dashboard with locations, fees, reports).
 * "auto" = franchisor when the prospect has two or more locations.
 */
export const OrgType = z.enum(["auto", "organization", "franchisor"]);
export const resolveOrgType = (orgType, locationsCount) => orgType && orgType !== "auto" ? orgType : locationsCount >= 2 ? "franchisor" : "organization";
/** Progress of a background content discovery run, kept on the prospect. */
export const ContentJob = z.object({
    status: z.enum(["running", "done", "failed"]),
    /** "shared" | "all" | a location id. */
    scope: z.string().default("all"),
    progress: z.number().int().min(0).max(100).default(0),
    message: z.string().default(""),
    startedAt: z.string(),
    finishedAt: z.string().optional(),
    error: z.string().optional(),
});
//# sourceMappingURL=content.js.map